/* ==========================================================================
   Product page — gallery and variant picker
   ========================================================================== */

(function () {
  'use strict';

  /* --- Gallery ------------------------------------------------------------ */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-media-id]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-media-target]'));

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.show(thumb.dataset.mediaTarget));
      });

      this.addEventListener('click', (event) => {
        const image = event.target.closest('[data-zoomable]');
        if (image) this.openLightbox(image);
      });
    }

    show(mediaId) {
      this.items.forEach((item) => {
        item.classList.toggle('is-hidden', item.dataset.mediaId !== mediaId);
      });
      this.thumbs.forEach((thumb) => {
        thumb.classList.toggle('is-active', thumb.dataset.mediaTarget === mediaId);
      });

      // Pause any video that just left the frame.
      this.querySelectorAll('.is-hidden video').forEach((video) => video.pause());
    }

    /** A minimal full-bleed viewer. Escape or a click closes it. */
    openLightbox(image) {
      const overlay = document.createElement('div');
      overlay.className = 'lightbox';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');

      const large = document.createElement('img');
      large.src = image.currentSrc || image.src;
      large.alt = image.alt;
      overlay.appendChild(large);

      function close() {
        overlay.remove();
        document.body.classList.remove('is-locked');
        document.removeEventListener('keydown', onKey);
      }

      function onKey(event) {
        if (event.key === 'Escape') close();
      }

      overlay.addEventListener('click', close);
      document.addEventListener('keydown', onKey);
      document.body.classList.add('is-locked');
      document.body.appendChild(overlay);
      requestAnimationFrame(() => overlay.classList.add('is-open'));
    }
  }

  customElements.define('product-gallery', ProductGallery);

  /* --- Variant picker ------------------------------------------------------ */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variant-data]');
      if (!data) return;

      try {
        this.variants = JSON.parse(data.textContent);
      } catch (error) {
        return;
      }

      this.section = document.querySelector('[data-product-section]');
      this.addEventListener('change', () => this.onChange());
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const selected = this.selectedOptions();
      const variant = this.variants.find((candidate) =>
        candidate.options.every((option, index) => option === selected[index])
      );

      this.updateLegends(selected);
      this.updateAvailability(selected);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateUrl(variant);
      this.updateInput(variant);
      this.updatePrice(variant);
      this.updateSku(variant);
      this.updateButton(variant);
      this.updateMedia(variant);
    }

    updateLegends(selected) {
      this.querySelectorAll('[data-option-value]').forEach((el) => {
        const index = parseInt(el.dataset.optionValue, 10);
        if (selected[index]) el.textContent = selected[index];
      });
    }

    /**
     * Grey out option values that cannot be reached from the current
     * selection, so a shopper never lands on a combination that does not exist.
     */
    updateAvailability(selected) {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));

      fieldsets.forEach((fieldset, optionIndex) => {
        fieldset.querySelectorAll('input').forEach((input) => {
          const probe = selected.slice();
          probe[optionIndex] = input.value;

          const match = this.variants.find((candidate) =>
            candidate.options.every((option, index) => {
              if (index === optionIndex) return option === input.value;
              if (probe[index] === null) return true;
              return option === probe[index];
            })
          );

          const label = fieldset.querySelector('label[for="' + input.id + '"]');
          if (!label) return;
          label.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    updateUrl(variant) {
      if (!this.dataset.url) return;
      window.history.replaceState({}, '', this.dataset.url + '?variant=' + variant.id);
    }

    updateInput(variant) {
      const input = document.querySelector('[data-variant-id]');
      if (!input) return;
      input.value = variant.id;
      input.disabled = false;
    }

    updatePrice(variant) {
      const block = document.querySelector('[data-price-block] .price');
      if (!block) return;

      const now = block.querySelector('.price__now');
      const was = block.querySelector('.price__was');

      if (now) now.textContent = window.theme.formatMoney(variant.price);

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      block.classList.toggle('price--on-sale', Boolean(onSale));

      if (onSale) {
        if (was) {
          was.textContent = window.theme.formatMoney(variant.compare_at_price);
        } else {
          const el = document.createElement('s');
          el.className = 'price__was';
          el.textContent = window.theme.formatMoney(variant.compare_at_price);
          block.appendChild(el);
        }
      } else if (was) {
        was.remove();
      }
    }

    updateSku(variant) {
      const sku = document.querySelector('[data-variant-sku]');
      if (sku) sku.textContent = variant.sku || '';
    }

    updateButton(variant) {
      const button = document.querySelector('[data-add-to-cart]');
      if (!button) return;
      const label = button.querySelector('[data-add-label]');

      if (variant.available) {
        button.removeAttribute('aria-disabled');
        button.disabled = false;
        if (label) {
          label.textContent =
            button.dataset.usePreorder === 'true'
              ? button.dataset.labelPreorder
              : button.dataset.labelAdd;
        }
      } else {
        button.setAttribute('aria-disabled', 'true');
        button.disabled = true;
        if (label) label.textContent = button.dataset.labelSoldout;
      }
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const gallery = document.querySelector('product-gallery');
      if (gallery && typeof gallery.show === 'function') {
        gallery.show(String(variant.featured_media.id));
      }
    }

    setUnavailable() {
      const button = document.querySelector('[data-add-to-cart]');
      if (!button) return;
      const label = button.querySelector('[data-add-label]');
      button.setAttribute('aria-disabled', 'true');
      button.disabled = true;
      if (label) label.textContent = window.theme.strings.unavailable;
    }
  }

  customElements.define('variant-picker', VariantPicker);

  /* --- Sticky buy bar ------------------------------------------------------ */

  (function stickyBar() {
    const bar = document.querySelector('[data-product-sticky]');
    const buy = document.querySelector('.product__buy');
    if (!bar || !buy) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          // Show the bar only once the real buy button has scrolled out of view.
          const entry = entries[0];
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', scrolledPast);
        },
        { threshold: 0 }
      );
      observer.observe(buy);
    }

    const trigger = bar.querySelector('[data-sticky-add]');
    if (!trigger) return;

    trigger.addEventListener('click', () => {
      const form = document.querySelector('product-form form');
      if (!form) return;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    });
  })();
})();
